import { Controller } from "@hotwired/stimulus"

// Read-only outline interactions for the PUBLIC shared-list page: expand/
// collapse a task's children, fold/unfold all, and show/hide completed —
// mirroring the signed-in tasks view but purely client-side (no server sync,
// no editing). Attached to a wrapper around both the toolbar and the outline.
export default class extends Controller {
    static targets = ["outline"]

    connect() {
        // The thread (task-comments) reports its new count after a write.
        this._onCommentsCount = (event) => {
            const { taskId, count } = event.detail || {}
            const chip = this.outlineTarget.querySelector(`[data-task-row][data-task-id="${CSS.escape(String(taskId))}"] [data-task-comments-chip]`)
            if (!chip) return
            const n = Number(count) || 0
            chip.classList.toggle("is-empty", n === 0)
            chip.innerHTML = `<i class="bi bi-chat-left-text" aria-hidden="true"></i>${n ? `<span>${n}</span>` : ""}`
            chip.title = n ? `${n} ${n === 1 ? "comment" : "comments"}` : "Comment"
        }
        window.addEventListener("jane:task-comments-count", this._onCommentsCount)
        this._openCommentsFromUrl()
    }

    // A comment notification links here with ?focus=<task>&comments=1 for a
    // view-only member: open that row's thread inline and bring it into view.
    _openCommentsFromUrl() {
        let id = null
        try {
            const params = new URLSearchParams(window.location.search)
            if (params.get("comments") !== "1") return
            id = params.get("focus")
        } catch { return }
        const chip = id && this.outlineTarget.querySelector(`[data-task-row][data-task-id="${CSS.escape(id)}"] [data-task-comments-chip]`)
        if (!chip) return
        chip.click()
        chip.closest("[data-task-row]")?.scrollIntoView({ block: "center" })
    }

    disconnect() {
        window.removeEventListener("jane:task-comments-count", this._onCommentsCount)
    }

    // A member with view access: open/close the row's comment thread inline.
    // Fetched fresh on every open - it is live data, not page state.
    toggleComments(event) {
        const chip = event.currentTarget
        const row = chip.closest("[data-task-row]")
        const holder = row && this.outlineTarget.querySelector(`[data-comments-for="${CSS.escape(row.dataset.taskId)}"]`)
        if (!holder) return
        if (!holder.hidden) {
            holder.hidden = true
            holder.replaceChildren()
            return
        }
        holder.hidden = false
        holder.innerHTML = '<div class="msg-detail-loading"><span>Loading…</span></div>'
        fetch(chip.dataset.commentsUrl, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((html) => { holder.innerHTML = html })
            .catch(() => { holder.innerHTML = '<div class="msg-detail-loading msg-detail-loading--error"><span>Could not load comments.</span></div>' })
    }

    // Caret click on a single row.
    toggleCollapse(event) {
        const row = event.target.closest("[data-task-row]")
        if (!row) return
        this._setCollapsed(row, row.dataset.collapsed !== "true")
    }

    // Fold-all / unfold-all round toggle.
    toggleFold(event) {
        const btn = event.currentTarget
        const folded = btn.dataset.folded !== "true"
        btn.dataset.folded = String(folded)
        btn.setAttribute("aria-pressed", String(folded))
        btn.title = folded ? "Unfold all" : "Fold all"
        this.outlineTarget.querySelectorAll("[data-task-row]").forEach((row) => this._setCollapsed(row, folded))
    }

    // Show/hide completed round toggle (default: shown).
    toggleCompleted(event) {
        const btn = event.currentTarget
        const hiding = this.outlineTarget.classList.toggle("is-hiding-completed")
        btn.classList.toggle("is-active", hiding)
        btn.title = hiding ? "Show completed" : "Hide completed"
        btn.setAttribute("aria-pressed", String(hiding))
    }

    _setCollapsed(row, next) {
        const kids = this.outlineTarget.querySelector(`[data-children-for="${CSS.escape(row.dataset.taskId)}"]`)
        if (!kids) return // no children — nothing to collapse
        row.dataset.collapsed = String(next)
        kids.classList.toggle("is-collapsed", next)
    }
}
